import React, { useCallback, useMemo, useState } from 'react';
import {
  Content,
  ContentHeader,
  CreateButton,
  PageWithHeader,
  SupportButton,
} from '@backstage/core-components';
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  DefaultCatalogPageProps,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { CatalogTable } from './CatalogTable';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
} from '@backstage/plugin-catalog-react';
import { FeaturedCatalogPage } from '@internal/backstage-plugin-featured-catalog';
import { EntityBUPicker } from './EntityBUPicker';
import { ViewMode, ViewModePicker } from './ViewModePicker';
import { CatalogGrid } from './CatalogGrid';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllowedOrDefault } from './CatalogGrid/CatalogGrid';
import { EntityProcessingStatusPicker } from './EntityProcessingStatusPicker/EntityProcessingStatusPicker';
import { UserListPicker } from './UserListPicker';

export function CustomCatalogPage(
  props: DefaultCatalogPageProps & {
    viewMode?: ViewMode;
  },
) {
  const {
    columns,
    actions,
    initiallySelectedFilter = 'all',
    viewMode = ViewMode.MODE_GRID,
  } = props;
  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';
  const createComponentLink = useRouteRef(
    catalogPlugin.externalRoutes.createComponent,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const queryViewMode: ViewMode = getAllowedOrDefault<ViewMode>(
    viewMode,
    queryParams.get('viewMode') as ViewMode,
    [ViewMode.MODE_GRID, ViewMode.MODE_TABLE],
  );
  const [mode, setViewMode] = useState<ViewMode>(queryViewMode);
  const updateViewMode = (vm: ViewMode) => {
    setViewMode(vm);
    queryParams.set('viewMode', vm);
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true,
    });
  };
  const allowedHeaderKinds = ['Component', 'System', 'API', 'Resource', 'User'];

  const forceGridRerender = useCallback(() => {
    // This is a hack to force the grid to re-render when the page is changed
    setTimeout(() => {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set('page', '0');

      navigate(`${location.pathname}?${currentParams.toString()}`, {
        replace: true,
      });
    }, 0);
  }, [location.pathname, navigate]);

  return (
    <PageWithHeader title={`${orgName} Catalog`} themeId="home">
      <EntityListProvider>
        <Content>
          <FeaturedCatalogPage />
          <ContentHeader
            titleComponent={
              <EntityKindPicker allowedKinds={allowedHeaderKinds} />
            }
          >
            <RequirePermission
              permission={catalogEntityCreatePermission}
              errorPage={<></>}
            >
              <CreateButton
                title="Create Component"
                to={createComponentLink && createComponentLink()}
              />
            </RequirePermission>
            <SupportButton>All your software catalog entities</SupportButton>
          </ContentHeader>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntityTypePicker />
              <UserListPicker
                initialFilter={initiallySelectedFilter}
                onListChange={forceGridRerender}
              />
              <EntityBUPicker />
              <EntityOwnerPicker />
              <EntityLifecyclePicker />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
              <EntityNamespacePicker />
              <ViewModePicker
                selected={mode}
                onChange={vm => updateViewMode(vm)}
              />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              {mode === ViewMode.MODE_TABLE && (
                <CatalogTable columns={columns} actions={actions} />
              )}
              {mode === ViewMode.MODE_GRID && <CatalogGrid actions={actions} />}
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </EntityListProvider>
    </PageWithHeader>
  );
}
